// cache.service.ts
import { Injectable } from '@angular/core';

type LookupKey =
  | 'paymentTerms'
  | 'taxStatus'
  | 'portAgent'
  | 'freightForwarder'
  | 'modeOfTransport'
  | 'packaging'
  | 'branchAddress'
  | 'supplierSetup'
  | 'setupBundle'
  | 'notificationEmails'
  | 'poAttachmentTypes'
  | 'poDeclineReasons'
  | 'approvalStages'
  | 'buyerAboutData'
  | 'buyerAccountData'
  | 'buyerTaxDetails'
  | 'quoteDetailsBundle'
  | 'roleData'
  | 'userListBundle'
  | 'roleMasterList'
  | 'userInbox'
  | 'sidenavMailCount'
  | 'rfqPreview'
  | 'quoteLineItemsBundle'
  | 'quoteHeaderBundle'
  | 'quotePaymentBundle'
  | 'quotePreviewBundle';

interface CacheEntry<T = any> {
  data: T;
  scope: string | number;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class RfqCacheService {
  private impa: any;
  private rfqFormId: any;
  private headerDataImpa: any;
  private lineItemDataImpa: any;
  private paymentTermsDataImpa: any;
  private lineItemTableDataImpa:any;

  private lookupCache = new Map<string, CacheEntry>();
  private defaultTtlMs = 10 * 60 * 1000;

  set rfqId(rfqFormId: any) {
    this.rfqFormId = rfqFormId;
  }

  get rfqId() {
    return this.rfqFormId;
  }

  set impaToRaiseRfq(impaItem: any) {
    this.impa = impaItem
  }

  set headerData(data: any) {
    this.headerDataImpa = data;
  }

  set lineItemData(data: any) {
    this.lineItemDataImpa = data;
  }

  set paymentTermsData(data: any) {
    this.paymentTermsDataImpa = data;
  }

  set lineItemTableData(data: any) {
    this.lineItemTableDataImpa = data;
  }


  get paymentTermsData() {
    return this.paymentTermsDataImpa;
  }

  get headerData() {
    return this.headerDataImpa;
  }

  get lineItemData() {
    return this.lineItemDataImpa;
  }

  get impaToRaiseRfq() {
    return this.impa;
  }

  get lineItemTableData(){
    return this.lineItemTableDataImpa
  }

  getLookup<T = any>(key: LookupKey, scope: string | number = 'global'): T | undefined {
    const entry = this.lookupCache.get(this.cacheKey(key, scope));
    if (!entry) return undefined;
    if (Date.now() > entry.expiresAt) {
      this.lookupCache.delete(this.cacheKey(key, scope));
      return undefined;
    }
    return entry.data as T;
  }

  setLookup<T = any>(key: LookupKey, scope: string | number, data: T, ttlMs?: number) {
    this.lookupCache.set(this.cacheKey(key, scope), {
      data,
      scope,
      expiresAt: Date.now() + (ttlMs ?? this.defaultTtlMs),
    });
  }

  async getOrFetchLookup<T = any>(
    key: LookupKey,
    scope: string | number,
    fetcher: () => Promise<T>,
    ttlMs?: number
  ): Promise<T> {
    const cached = this.getLookup<T>(key, scope);
    if (cached !== undefined) return cached;
    const data = await fetcher();
    this.setLookup<T>(key, scope, data, ttlMs);
    return data;
  }

  invalidateLookup(key: LookupKey, scope?: string | number) {
    if (scope === undefined) {
      for (const k of Array.from(this.lookupCache.keys())) {
        if (k.startsWith(`${key}::`)) this.lookupCache.delete(k);
      }
      return;
    }
    this.lookupCache.delete(this.cacheKey(key, scope));
  }

  private cacheKey(key: LookupKey, scope: string | number): string {
    return `${key}::${scope}`;
  }
}
